import {AfterViewInit, Component, Inject, OnInit, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {SelectListProductConfiguration} from '@domain/product-configuration/select-list-product-configuration';
import {SelectListProductConfigurationVersionHistory} from '@domain/product-configuration/select-list-product-configuration-version-history';
import {WeaveStructure} from '@domain/textile-data/weave-structure/weave-structure';
import {PRODUCT_CONFIGURATIONS, ProductConfigurations} from '@infrastructure/http/product-configuration/product-configuration.interface';
import {GridProductionStatusComponent} from '@presentation/components/grid-production-status/grid-production-status.component';
import {NavigationMachineOverviewData} from '@presentation/pages/machine-overview/navigation-machine-overview-data.interface';
import {
  BaseComponent,
  ColDefBuilderFactoryService,
  DateTimeFormat,
  DateTimePipe,
  DialogComponentData,
  GridOptionsBuilderFactoryService,
  SelectGridDialog,
  StringUtils,
  TranslateService
} from '@vdw/angular-component-library';
import {AgGridAngular} from 'ag-grid-angular';
import {ColDef, FirstDataRenderedEvent, GetQuickFilterTextParams, GridApi, GridOptions, GridReadyEvent, RowDoubleClickedEvent, RowSelectedEvent, ValueGetterParams} from 'ag-grid-community';
import {takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-select-product-configuration',
  templateUrl: './select-product-configuration.component.html',
  styleUrls: ['./select-product-configuration.component.scss']
})
export class SelectProductConfigurationComponent extends BaseComponent implements OnInit, AfterViewInit, SelectGridDialog {
  @ViewChild('productConfigurationsGrid') public productConfigurationsGrid: AgGridAngular;
  @ViewChild('productConfigurationVersionsHistoryGrid') public productConfigurationVersionsHistoryGrid: AgGridAngular;
  public listOfGridOptions: GridOptions[];
  public listOfGridApis: GridApi[];
  public listOfProductConfigurations: SelectListProductConfiguration[];
  public isSelectingSpecificVersion = false;
  public selectedProductConfiguration: SelectListProductConfiguration;

  private machineId: number;

  public constructor(
    @Inject(MAT_DIALOG_DATA) {machineId}: {machineId: number},
    @Inject(PRODUCT_CONFIGURATIONS) private readonly productConfigurations: ProductConfigurations,
    private readonly translateService: TranslateService,
    private readonly gridOptionsBuilderFactory: GridOptionsBuilderFactoryService,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly matDialogRef: MatDialogRef<SelectProductConfigurationComponent, {productConfiguration: SelectListProductConfiguration; version: number}>,
    private readonly navigationHelperService: NavigationHelperService<DialogComponentData<typeof SelectProductConfigurationComponent>>,
    private readonly dateTimePipe: DateTimePipe
  ) {
    super();
    this.machineId = machineId;
  }

  public get gridOptionsForProductConfiguration(): GridOptions {
    return this.listOfGridOptions[0];
  }

  public get gridOptionsForProductConfigurationVersionHistory(): GridOptions {
    return this.listOfGridOptions[1];
  }

  public ngOnInit(): void {
    this.initializeGridOptions();

    this.productConfigurations
      .getPossibleProductConfigurationsForMachine(this.machineId)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((productConfigurations: SelectListProductConfiguration[]) => {
        this.listOfProductConfigurations = productConfigurations;
      });
  }

  public ngAfterViewInit(): void {
    this.listOfGridApis = [this.productConfigurationsGrid?.api, this.productConfigurationVersionsHistoryGrid?.api];
  }

  public onNavigationHelperDestroy(): void {
    this.navigationHelperService.savePartialState<DialogComponentData<typeof SelectProductConfigurationComponent>>({dialogComponent: SelectProductConfigurationComponent});
  }

  public getTitle(): string {
    return this.isSelectingSpecificVersion
      ? this.translateService.instant('PRODUCTION_ORDER.SELECT_PRODUCT_CONFIGURATION.SELECT_VERSION_OF', {name: this.selectedProductConfiguration.name})
      : this.translateService.instant('GENERAL.ACTIONS.SELECT_OBJECT', {object: this.translateService.instant('PRODUCT_CONFIGURATION.PRODUCT_CONFIGURATION', {count: 1}).toLowerCase()});
  }

  public selectProductConfiguration(): void {
    let version = null;

    if (this.productConfigurationVersionsHistoryGrid?.api) {
      version = this.productConfigurationVersionsHistoryGrid.api.getSelectedRows()[0].version;
    } else {
      version =
        this.selectedProductConfiguration.versionHistories.find((versionHistory: SelectListProductConfigurationVersionHistory) => versionHistory.readyForProduction)?.version ??
        this.selectedProductConfiguration.versionHistories[0].version;
    }

    this.matDialogRef.close({productConfiguration: this.selectedProductConfiguration, version});
  }

  public selectSpecificVersion(): void {
    this.isSelectingSpecificVersion = true;
  }

  public cancelSpecificVersionSelection(): void {
    this.isSelectingSpecificVersion = false;
  }

  public navigateToAddProductConfiguration(): void {
    this.navigationHelperService.navigateToNextRouteWithPartialState({equipmentId: this.machineId} as NavigationMachineOverviewData, RouteUtils.paths.texEdit.addProductConfiguration.absolutePath);
  }

  private initializeGridOptions(): void {
    this.listOfGridOptions = [
      this.gridOptionsBuilderFactory
        .getBuilder(this.getColumnDefsForProductConfiguration(), GridIdentifier.SELECT_PRODUCT_CONFIGURATION)
        .withColumnMenu()
        .withOnFirstDataRendered(({api}: FirstDataRenderedEvent) => {
          if (this.selectedProductConfiguration) {
            api.getRowNode(`${this.selectedProductConfiguration.id}`)?.setSelected(true);
          }
        })
        .withOnRowSelected(({node}: RowSelectedEvent) => {
          this.selectedProductConfiguration = node?.data;
        })
        .withOnRowDoubleClicked((event: RowDoubleClickedEvent) => {
          event.node.setSelected(true);
          this.selectProductConfiguration();
        })
        .withLoadingOverlay({scale: 0.7})
        .withNoRowsOverlay({
          titleParam: 'PRODUCT_CONFIGURATION.PRODUCT_CONFIGURATION',
          hideDescription: true,
          isAnyFilterPresent: () => this.productConfigurationsGrid?.api?.isAnyFilterPresent(),
          scale: 0.7
        })
        .build(),
      this.gridOptionsBuilderFactory
        .getBuilder(this.getColumnDefsForProductConfigurationVersionHistory(), GridIdentifier.SELECT_PRODUCT_CONFIGURATION_VERSION_HISTORY)
        .withColumnMenu()
        .withOnGridReady(({api}: GridReadyEvent) => {
          const versionHistories = this.selectedProductConfiguration.versionHistories;

          let versionHistoryToSelectIndex = versionHistories.findIndex((versionHistory: SelectListProductConfigurationVersionHistory) => versionHistory.readyForProduction);

          versionHistoryToSelectIndex = versionHistoryToSelectIndex === -1 ? 0 : versionHistoryToSelectIndex;

          api.getRowNode(`${versionHistoryToSelectIndex}`).setSelected(true);
        })
        .withNoRowsOverlay({
          isAnyFilterPresent: () => this.productConfigurationVersionsHistoryGrid?.api?.isAnyFilterPresent(),
          scale: 0.7
        })
        .withoutGetRowId()
        .build()
    ];
  }

  private getColumnDefsForProductConfiguration(): ColDef[] {
    return [
      this.colDefBuilderFactoryService.getBuilder().withField('name').withHeaderName('GENERAL.NAME').withComparator(StringUtils.stringComparator).build(),
      this.colDefBuilderFactoryService.getBuilder().withField('machine.name', true).withHeaderName('MACHINE.MACHINE').withComparator(StringUtils.stringComparator).build(),
      this.colDefBuilderFactoryService.getBuilder().withField('weaveQuality.name').withHeaderName('TEXTILE_DATA.MACHINE_QUALITY.MACHINE_QUALITY').withComparator(StringUtils.stringComparator).build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('weaveQuality.weaveStructureName')
        .withHeaderName('TEXTILE_DATA.WEAVE_STRUCTURE.WEAVE_STRUCTURE')
        .withComparator(StringUtils.stringComparator)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('readyForProduction')
        .withHeaderName('GENERAL.STATUS')
        .withCellRenderer(GridProductionStatusComponent)
        .withoutFilter()
        .withSortable()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('helpWeaveStructures')
        .withHeaderName('PRODUCT_CONFIGURATION.HELP_WEAVE_STRUCTURE')
        .withValueGetter((params: ValueGetterParams) => params.data.helpWeaveStructures.map((helpWeaveStructure: WeaveStructure) => helpWeaveStructure.name).join(', '), true)
        .withFilterValueGetter((params: ValueGetterParams) =>
          params.data.helpWeaveStructures.length > 0 ? params.data.helpWeaveStructures.map((helpWeaveStructure: WeaveStructure) => helpWeaveStructure.name) : null
        )
        .withComparator(StringUtils.stringComparator)
        .withHide()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withHeaderName('DESIGN_LIBRARY.DESIGN', 2, true)
        .withValueGetter((params: ValueGetterParams) => params.data.designs.length, true)
        .withRightAlignment()
        .withSortable(false)
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService.getBuilder().withField('description', true).withHeaderName('GENERAL.DESCRIPTION').withComparator(StringUtils.stringComparator).build(),
      this.colDefBuilderFactoryService.getBuilder().withField('dateModified').withHeaderName('GENERAL.DATETIME.DATE').withDate().withDateMultiFilter().build(),
      this.colDefBuilderFactoryService.getBuilder().withField('colorSet.name', true).withHeaderName('TEXTILE_DATA.COLOR_SET.COLOR_SET').withHide().withComparator(StringUtils.stringComparator).build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('weftColorSet.name', true)
        .withHeaderName('PRODUCT_CONFIGURATION.WEFT_COLOR_SET')
        .withHide()
        .withComparator(StringUtils.stringComparator)
        .build()
    ];
  }

  private getColumnDefsForProductConfigurationVersionHistory(): ColDef[] {
    return [
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('dateModified')
        .withHeaderName('PRODUCTION_ORDER_LITE.VERSION_HISTORY.DATE_TIME')
        .withDate()
        .withGetQuickFilterText((params: GetQuickFilterTextParams) => this.dateTimePipe.transform(params.data.dateModified, DateTimeFormat.DATETIME_SHORT))
        .build(),
      this.colDefBuilderFactoryService.getBuilder().withField('version').withHeaderName('GENERAL.VERSION').withRightAlignment().build(),
      this.colDefBuilderFactoryService.getBuilder().withField('changes').withHeaderName('PRODUCTION_ORDER_LITE.VERSION_HISTORY.CHANGES').withComparator(StringUtils.stringComparator).build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('additionalInformation')
        .withHeaderName('PRODUCTION_ORDER_LITE.VERSION_HISTORY.ADDITIONAL_INFORMATION')
        .withComparator(StringUtils.stringComparator)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('readyForProduction')
        .withHeaderName('GENERAL.STATUS')
        .withCellRenderer(GridProductionStatusComponent)
        .withGetQuickFilterText(() => '')
        .build()
    ];
  }
}
